import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/components/SearchInput.module.css";
import { ReactComponent as GlassIcon } from "../../assets/icons/glass.svg";

function toCssSize(v) {
  if (v == null) return undefined;
  if (typeof v === "number") return `${v}px`;
  return String(v);
}

function radiusFromHeight(height) {
  if (height == null) return undefined;
  const n =
    typeof height === "number"
      ? height
      : Number(String(height).replace("px", ""));
  if (!Number.isFinite(n)) return undefined;
  return `${Math.round(n / 2)}px`;
}

export default function SearchInput({
  value,
  onChange,
  onSubmit,
  onSearch,
  placeholder = "Buscar...",
  minChars = 3,
  debounceMs = 300,
  width,
  height,
}) {
  const isControlled = value != null;
  const [innerValue, setInnerValue] = useState("");
  const v = isControlled ? value : innerValue;

  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const canSearch = useMemo(
    () => (v || "").trim().length >= minChars,
    [v, minChars],
  );

  // Última búsqueda emitida: garantiza una sola emisión por valor aunque se
  // combinen el debounce y Enter, y habilita el reset al borrar el texto.
  const lastEmittedSearchRef = useRef("");
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    if (!onSearch) return;

    if (canSearch) {
      if (v === lastEmittedSearchRef.current) return;

      debounceTimeoutRef.current = setTimeout(() => {
        lastEmittedSearchRef.current = v;
        onSearch(v);
      }, debounceMs);

      return () => clearTimeout(debounceTimeoutRef.current);
    }

    // Si el texto baja del mínimo después de haber buscado (p.ej. se borra),
    // se emite una búsqueda vacía para que el consumidor resetee su filtro.
    if (lastEmittedSearchRef.current === "") return;

    debounceTimeoutRef.current = setTimeout(() => {
      lastEmittedSearchRef.current = "";
      onSearch("");
    }, debounceMs);

    return () => clearTimeout(debounceTimeoutRef.current);
  }, [v, canSearch, onSearch, debounceMs]);

  function setValue(next) {
    if (!isControlled) setInnerValue(next);
    onChange?.(next);
  }

  const showPlaceholderIcon = !isFocused && !(v || "").length;

  const rootStyle = {
    width: toCssSize(width),
    height: toCssSize(height),
    borderRadius: radiusFromHeight(height),
  };

  return (
    <div
      className={styles.searchInput}
      style={rootStyle}
      data-state={isFocused ? "typing" : "default"}
    >
      <div className={styles.left}>
        {showPlaceholderIcon ? (
          <span className={styles.iconWrap} aria-hidden="true">
            <GlassIcon className={styles.icon} />
          </span>
        ) : null}

        <input
          ref={inputRef}
          className={styles.input}
          type="search"
          value={v}
          placeholder={placeholder}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSubmit?.(v);
              if (onSearch && canSearch && v !== lastEmittedSearchRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                lastEmittedSearchRef.current = v;
                onSearch(v);
              }
            }
          }}
        />
      </div>
    </div>
  );
}
